import React, { Suspense, lazy } from 'react';
import { Navigate, createBrowserRouter, RouterProvider } from 'react-router-dom';
import AuthGuard from './AuthGuard';
import FullScreenLoader from '../components/FullScreenLoader';
import AppRouteErrorFallback from '../components/AppRouteErrorFallback';

// Layouts
const MainLayout = lazy(() => import('../layout/MainLayout'));
const AuthLayout = lazy(() => import('../layout/AuthLayout'));
// Auth Pages
const Login = lazy(() => import('../pages/Login'));
const LinkedinLogin = lazy(() => import('../pages/Login/LinkedinLogin'));
const Register = lazy(() => import('../pages/Register'));
const OnBoarding = lazy(() => import('../pages/OnBoarding'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const OTPVerification = lazy(() => import('../pages/OTPVerification'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const ChangePassword = lazy(() => import('../pages/ChangePassword'));

// Main Pages
const Home = lazy(() => import('../pages/Home'));
const VideoScreen = lazy(() => import('../pages/VideoScreen'));
const KnowledgeHub = lazy(() => import('../pages/KnowledgeHub'));
const KnowledgeHubDetail = lazy(() => import('../pages/KnowledgeHub/KnowledgeHubDetail'));
const ProfileCreation = lazy(() => import('../pages/ProfileCreation'));
const ProfileDetails = lazy(() => import('../pages/ProfileDetails'));
const ViewProfile = lazy(() => import('../pages/ViewProfile'));
const FreelancerProfilePage = lazy(() => import('../pages/Freelancer/FreelancerProfilePage'));
const CreatorOnboarding = lazy(() => import('../pages/CreatorOnboarding'));
const CreatorOnboardingApplication = lazy(() => import('../pages/CreatorOnboarding/ViewCreatorApplication'));

// Package & Gigs
const CreatePackage = lazy(() => import('../pages/Package/CreatePackage'));
const PackagePreview = lazy(() => import('../pages/Package/PackagePreviewPage'));
const EditPackage = lazy(() => import('../pages/Package/EditPackage'));
const PackageList = lazy(() => import('../pages/Package/List/index'));
const PackageCoupons = lazy(() => import('../pages/PackageCoupons/PackageCouponsPage'));
const PackageDetails = lazy(() => import('../pages/Package/PackageDetailsPage'));
const BrowsePackages = lazy(() => import('../pages/BrowseProjects'));
const FreelancerPackagePreview = lazy(() => import('../pages/Package/FreelancerPackagePreviewPage'));
const MyJoinRequests = lazy(() => import('../pages/MyJoinRequests'));
const SearchTeamMember = lazy(() => import('../pages/SearchTeamMember/SearchTeamMemberPage'));
const JobPostingsPage = lazy(() => import('../pages/JobPostings/JobPostingsPage'));
const MyInvitations = lazy(() => import('../pages/MyInvitations'));
const MyOrders = lazy(() => import('../pages/MyOrders'));
const OrderDetails = lazy(() => import('../pages/MyOrders/OrderDetailsPage'));
const MyProjects = lazy(() => import('../pages/MyProjects'));
const Finance = lazy(() => import('../pages/Finance'));
const PackageInquiries = lazy(() => import('../pages/Messages/PackageInquiriesPage'));
const NotificationsPage = lazy(() => import('../pages/Notifications/NotificationsPage'));
const ProjectDetails = lazy(() => import('../pages/MyProjects/ProjectDetailsPage'));
const TaskList = lazy(() => import('../pages/MyProjects/TaskListPage'));
const TaskDetails = lazy(() => import('../pages/MyProjects/TaskDetailsPage'));
const MyTasks = lazy(() => import('../pages/MyTasks'));
const OverdueTasks = lazy(() => import('../pages/OverdueTasks'));
const TeamInvitation = lazy(() => import('../pages/TeamInvitation'));
const PlatformSettingsPage = lazy(() => import('../pages/PlatformSettings/PlatformSettingsPage'));
const CreateGigs = lazy(() => import('../pages/Gigs/CreateGigs'));
const GigsPreview = lazy(() => import('../pages/Gigs/GigsPreview'));
const GigsList = lazy(() => import('../pages/Gigs/List/index'));
const GigsDetails = lazy(() => import('../pages/Gigs/GigsDetails'));

// Error Page
const ErrorPage = lazy(() => import('../pages/ErrorPage'));

// Helper component to wrap with Suspense
const LazyComponent = ({ component: Component }: { component: React.ComponentType }) => (
  <Suspense fallback={<FullScreenLoader visible={true} />}>
    <Component />
  </Suspense>
);

const CreatorOnlyComponent = ({ component: Component }: { component: React.ComponentType }) => {
  const roles = localStorage.getItem('ROLES') || '';

  if (!roles.includes('ROLE_CREATOR')) {
    return <Navigate to="/freelancer/home" replace />;
  }

  return <LazyComponent component={Component} />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <Navigate to="/freelancer" replace />,
    errorElement: <AppRouteErrorFallback />,
  },
  // Direct team invitation route (for backward compatibility with old email links)
  {
    path: '/team-invitation/:token',
    element: <LazyComponent component={TeamInvitation} />,
    errorElement: <AppRouteErrorFallback />,
  },
  {
    path: '/freelancer',
    errorElement: <AppRouteErrorFallback />,
    children: [
      // Auth routes
      {
        element: <LazyComponent component={AuthLayout} />,
        children: [
          { path: 'auth/login', element: <LazyComponent component={Login} /> },
          { path: 'auth/linkedin/callback', element: <LazyComponent component={LinkedinLogin} /> },
          { path: 'auth/register', element: <LazyComponent component={Register} /> },
          { path: 'auth/onboarding', element: <LazyComponent component={OnBoarding} /> },
          { path: 'auth/forgot-password', element: <LazyComponent component={ForgotPassword} /> },
          { path: 'auth/otp-verify', element: <LazyComponent component={OTPVerification} /> },
          { path: 'auth/reset-password', element: <LazyComponent component={ResetPassword} /> },
          { path: 'auth', element: <Navigate to="/freelancer/auth/login" replace /> },
        ],
      },
      // Public team invitation route (accessible without auth, but will redirect to login if needed)
      { path: 'team-invitation/:token', element: <LazyComponent component={TeamInvitation} /> },
      // Protected routes
      {
        element: (
          <AuthGuard>
            <Suspense fallback={<FullScreenLoader visible={true} />}>
              <MainLayout />
            </Suspense>
          </AuthGuard>
        ),
        children: [
          { path: 'home', element: <LazyComponent component={Home} /> },
          { path: 'tutorials', element: <LazyComponent component={VideoScreen} /> },
          { path: 'knowledge-hub', element: <LazyComponent component={KnowledgeHub} /> },
          { path: 'knowledge-hub/:id', element: <LazyComponent component={KnowledgeHubDetail} /> },
          { path: 'create-profile', element: <LazyComponent component={ProfileCreation} /> },
          { path: 'view-profile', element: <LazyComponent component={ViewProfile} /> },
          { path: 'profile-details', element: <LazyComponent component={ProfileDetails} /> },
          { path: 'profile/:id', element: <LazyComponent component={FreelancerProfilePage} /> },
          { path: 'creator-onboarding', element: <LazyComponent component={CreatorOnboarding} /> },
          {
            path: 'creator-onboarding/applications',
            element: <LazyComponent component={CreatorOnboardingApplication} />,
          },
          { path: 'create-package', element: <CreatorOnlyComponent component={CreatePackage} /> },
          { path: 'package-preview', element: <CreatorOnlyComponent component={PackagePreview} /> },
          { path: 'create-gigs', element: <CreatorOnlyComponent component={CreateGigs} /> },
          { path: 'gigs-preview', element: <CreatorOnlyComponent component={GigsPreview} /> },
          { path: 'package-details/:id', element: <LazyComponent component={PackageDetails} /> },
          { path: 'browse-packages', element: <LazyComponent component={BrowsePackages} /> },
          {
            path: 'browse-packages/:packageId',
            element: <LazyComponent component={FreelancerPackagePreview} />,
          },
          { path: 'my-join-requests', element: <LazyComponent component={MyJoinRequests} /> },
          { path: 'job-postings', element: <LazyComponent component={JobPostingsPage} /> },
          { path: 'search-team-member/:packageId', element: <LazyComponent component={SearchTeamMember} /> },
          { path: 'my-invitations', element: <LazyComponent component={MyInvitations} /> },
          { path: 'my-packages', element: <CreatorOnlyComponent component={PackageList} /> },
          { path: 'package-coupons', element: <CreatorOnlyComponent component={PackageCoupons} /> },
          { path: 'my-orders', element: <CreatorOnlyComponent component={MyOrders} /> },
          { path: 'my-orders/:orderId', element: <CreatorOnlyComponent component={OrderDetails} /> },
          { path: 'my-projects', element: <LazyComponent component={MyProjects} /> },
          { path: 'finance', element: <LazyComponent component={Finance} /> },
          { path: 'messages', element: <LazyComponent component={PackageInquiries} /> },
          { path: 'notifications', element: <LazyComponent component={NotificationsPage} /> },
          { path: 'my-projects/:projectId', element: <LazyComponent component={ProjectDetails} /> },
          { path: 'my-projects/:projectId/tasks', element: <LazyComponent component={TaskList} /> },
          { path: 'my-projects/:projectId/tasks/:taskId', element: <LazyComponent component={TaskDetails} /> },
          { path: 'my-tasks', element: <LazyComponent component={MyTasks} /> },
          { path: 'my-tasks/:taskId', element: <LazyComponent component={TaskDetails} /> },
          { path: 'overdue-tasks', element: <CreatorOnlyComponent component={OverdueTasks} /> },
          { path: 'package-list', element: <CreatorOnlyComponent component={PackageList} /> },
          { path: 'edit-package', element: <CreatorOnlyComponent component={EditPackage} /> },
          { path: 'gigs-list', element: <CreatorOnlyComponent component={GigsList} /> },
          { path: 'gigs-details', element: <CreatorOnlyComponent component={GigsDetails} /> },
          { path: 'platform-settings', element: <LazyComponent component={PlatformSettingsPage} /> },
          { path: 'change-password', element: <LazyComponent component={ChangePassword} /> },
          { path: '', element: <Navigate to="/freelancer/home" replace /> },
        ],
      },
      // 404 route
      { path: '*', element: <LazyComponent component={ErrorPage} /> },
    ],
  },
]);

// Create a provider component
export const RouterProviderComponent = () => <RouterProvider router={router} />;

export default router;
